/**
 * Single source of truth for card number (PAN) length and formatting rules.
 *
 * Two units are in play and they are easy to confuse, so they are named
 * explicitly throughout:
 *   - "digits"    : the bare PAN, e.g. 19 for a long Visa
 *   - "formatted" : what the user sees, digits plus grouping spaces, e.g. 23
 *
 * Conflating the two is what previously capped the input at 16 digits: a limit
 * of 19 was read as "19 digits" when it actually meant "16 digits + 3 spaces".
 */

// PANs run 12-19 digits (ISO/IEC 7812). AMEX is the one fixed-length exception.
export const MAX_PAN_DIGITS = 19;
export const AMEX_PAN_DIGITS = 15;
export const DEFAULT_MIN_PAN_DIGITS = 16;

const GROUP_SIZE = 4;

/** Strip grouping spaces (and anything else non-numeric) from a PAN. */
export function stripPan(value) {
    return String(value || "").replace(/\D/g, "");
}

/**
 * Group a PAN into blocks of four for display: "4000 0000 0000 0000 006".
 *
 * Strips to digits via stripPan first. The original inline version used
 * `[^\dA-Z]`, which let letters through and left this disagreeing with
 * stripPan about what a PAN contains — no PAN has letters in it.
 */
export function formatPan(value) {
    return stripPan(value)
        .replace(new RegExp(`(.{${GROUP_SIZE}})`, "g"), "$1 ")
        .trim();
}

/**
 * Formatted width of a PAN of `digits` digits, i.e. digits plus the grouping
 * spaces that sit between blocks (no trailing space).
 */
export function formattedLength(digits) {
    if (digits <= 0) return 0;
    return digits + Math.ceil(digits / GROUP_SIZE) - 1;
}

/**
 * Widest formatted value the card number input must accommodate. This is what
 * the field's maxlength has to be, so 19-digit PANs can be typed in full.
 */
export const MAX_PAN_FORMATTED_LENGTH = formattedLength(MAX_PAN_DIGITS);

/** True when the PAN is an AMEX, by prefix or by detected card type. */
export function isAmex(cardType, pan) {
    const digits = stripPan(pan);
    return /^3[47]/.test(digits) || cardType === "AMEX" || cardType === "AMERICAN";
}

/**
 * Minimum digit count before a PAN can be considered complete. This is a floor
 * for validation, not a target: brands issue variable-length PANs (Visa is 13,
 * 16 or 19), so reaching it does not mean the user has finished typing.
 */
export function expectedPanLength(cardType, pan) {
    return isAmex(cardType, pan) ? AMEX_PAN_DIGITS : DEFAULT_MIN_PAN_DIGITS;
}